import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, inject, Injector, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, FormControlDirective, FormControlName, FormGroupDirective, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { TooltipDirective } from '@logscl/ng2-tooltip-directive';
import { SELECT_ALL_ID, SelectWithSearchHelper } from './select-with-search.helper';
import { arraysEqual } from '../../helpers/array.helper';
import { SelectGroup, SelectItem, SelectItemType } from './select-item.type';
import { Selection } from './selection';
import { DEFAULT_DEBOUNCE_TIME } from '../../types/constants';

@Component({
  selector: 'shared-select-with-search',
  templateUrl: './select-with-search.component.html',
  styleUrls: ['./select-with-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, multi: true, useExisting: SelectWithSearchComponent }],
  standalone: false,
})
export class SelectWithSearchComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
  private readonly injector = inject(Injector);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly tooltipDirective = inject(TooltipDirective, { optional: true });

  @Input() selection: string | string[];
  @Input() items: SelectItem[] = [];
  @Input() fixedItems: SelectItem[] = [];
  @Input() multiple = false;
  @Input() iconKey: string;
  @Input() placeholder: string;
  @Input() searchPlaceholder = 'Search...';
  @Input() panelClass: string | string[] | Set<string> | Record<string, any>;
  @Input() selectClass: string | string[] | Set<string> | Record<string, any>;
  @Input() disabled = false;
  @Input() defaultValue: string | string[];
  @Input() tooltip: string;
  @Input() selectAllLabel: string;
  @Input() sortItemsByLabel = true;

  @Output() selectionChange = new EventEmitter<string | string[]>();
  @Output() selectionEnd = new EventEmitter<string | string[]>();

  @ViewChild(MatSelect, { static: false }) matSelect: MatSelect;

  groups: SelectGroup[] = [];
  triggerLabel: string;
  selectAllItem: SelectItem | null = null;
  fixedItemsWithoutGroup: SelectItem[] = [];

  get visibleItemCount(): number {
    return SelectWithSearchHelper.getVisibleRegularItems(this.items, this._filter).length;
  }

  get showSelectOnlyThese(): boolean {
    return !!this.selectAllItem && !!this.filter && this.visibleItemCount > 0;
  }

  get isSelectAllIndeterminate(): boolean {
    if (!this.multiple || !this.selectAllItem || !this.items?.length) return false;
    const visibleItems = SelectWithSearchHelper.getVisibleRegularItems(this.items, this._filter);
    if (visibleItems.length === 0) return false;
    const selectedIds = new Set(this.selectionModelAsArray.map(i => i?.id));
    const visibleSelectedCount = visibleItems.filter((item: SelectItem) => selectedIds.has(item.id)).length;
    if (visibleSelectedCount === 0) return false;
    const allItemsSelected = (this.items ?? []).every(i => selectedIds.has(i.id));
    return visibleSelectedCount < visibleItems.length || !allItemsSelected;
  }

  get selectionModel(): Selection {
    return this._selectionModel;
  }

  set selectionModel(value: Selection) {
    this.updateSelection(SelectWithSearchHelper.sanitizeSelection(value), false);
    this.notifyTouched();
    this.onChange(SelectWithSearchHelper.getSelectionIds(this._selectionModel, this.multiple));
  }

  get filter(): string {
    return this._filter;
  }

  set filter(value: string) {
    if (this.filterTimeout !== null) {
      clearTimeout(this.filterTimeout);
    }
    this.filterTimeout = window.setTimeout(() => {
      this._filter = value;
      this.filterItems();
      this.filterTimeout = null;
      this.cdr.markForCheck();
    }, DEFAULT_DEBOUNCE_TIME);
  }

  get isInvalid(): boolean {
    return !!(this.formControl?.invalid && this.formControl?.touched);
  }

  private _selectionModel: Selection;
  private _filter = '';
  private formControl: FormControl;
  private isInitialized = false;
  private filterTimeout: number | null = null;
  private onChange = (value: string | string[]): void => this.selectionChange.emit(value);
  private onTouched: () => void = null;

  constructor() {
    // Hide tooltip for whole component, display it only on select element
    if (this.tooltipDirective) {
      this.tooltipDirective.options = { ...this.tooltipDirective.options, display: false };
    }
  }

  ngOnInit(): void {
    this.initializeFormControl();
    this.createSelectAllItem();
    this.updateFixedItemsWithoutGroup();
    this.filterItems();
    const selectionFromIds = SelectWithSearchHelper.getSelectionFromIds(this.selection, this.items, this.fixedItems, this.selectAllItem, this.multiple);
    this.updateSelection(selectionFromIds, true);
    this.isInitialized = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.isInitialized) {
      return;
    }

    if (changes.items) {
      this.createSelectAllItem();
      this.filterItems();
      this.resyncSelectionAfterFilterChange();
    }

    if (changes.fixedItems) {
      this.updateFixedItemsWithoutGroup();
      this.filterItems();
      this.resyncSelectionAfterFilterChange();
    }

    if (changes.selectAllLabel) {
      this.createSelectAllItem();
      this.filterItems();
      this.resyncSelectionAfterFilterChange();
    }

    if (changes.selection && !changes.selection.firstChange) {
      // Prevent infinite loops by checking if selection actually changed
      const currentIds = this.normalizeToArray(this.getSelectionIds());
      const newIds = SelectWithSearchHelper.normalizeToArray(this.selection);

      if (!arraysEqual(currentIds, newIds)) {
        const selectionFromIds = SelectWithSearchHelper.getSelectionFromIds(this.selection, this.items, this.fixedItems, this.selectAllItem, this.multiple);
        this.updateSelection(selectionFromIds, true);
      }
    }
  }

  ngOnDestroy(): void {
    if (this.filterTimeout !== null) {
      clearTimeout(this.filterTimeout);
      this.filterTimeout = null;
    }
  }

  trackByIdAndIndex = (index: number, item: SelectItem): any => {
    // Include index to handle potential duplicate IDs in the same list
    return item ? `${item.id}_${index}` : index;
  };

  isFixedItemInGroup(item: SelectItem, group: SelectGroup): boolean {
    return group.fixedItems?.some(fi => fi.id === item.id) ?? false;
  }

  onClosed(): void {
    this.notifyTouched();
    this.selectionEnd.emit(this.getSelectionIds());
  }

  compareItems(o1: SelectItem, o2: SelectItem): boolean {
    return o1?.id === o2?.id;
  }

  resetToDefault(): void {
    const defaultSelection = this.getSelectionFromIds(this.defaultValue);
    this.updateSelection(defaultSelection, true);
    this.notifyTouched();
    this.onChange(this.getSelectionIds());
    this.selectionEnd.emit(this.getSelectionIds());
  }

  get canResetToDefault(): boolean {
    if (this.defaultValue === undefined) {
      return false;
    }

    const defaultIds = this.normalizeToArray(this.defaultValue);
    const selectedIds = this.normalizeToArray(this.getSelectionIds());

    // Filter out auto-synced SelectItemType.All items from comparison
    // These items are automatically added/removed by syncFixedItems() based on regular item selection
    const selectAllTypeItemIds = SelectWithSearchHelper.getSelectAllTypeItems(this.selectAllItem, this.fixedItems).map((item: SelectItem) => item.id);
    const userSelectedIds = selectedIds.filter(id => !selectAllTypeItemIds.includes(id));
    const userDefaultIds = defaultIds.filter(id => !selectAllTypeItemIds.includes(id));

    return !arraysEqual(userDefaultIds, userSelectedIds);
  }

  onSelectAllClick(selected: boolean): void {
    if (!this.multiple || !this.selectAllItem) {
      return;
    }

    const visibleItems = SelectWithSearchHelper.getVisibleRegularItems(this.items, this._filter);
    const currentSelection = this.selectionModelAsArray;
    const selectedIds = new Set(currentSelection.map(i => i.id));
    const visibleIds = new Set(visibleItems.map((item: SelectItem) => item.id));

    let newSelection: SelectItem[];
    if (!selected) {
      // SELECT_ALL was just deselected — remove visible items, keep hidden selections
      newSelection = currentSelection.filter(i =>
        i.id !== SELECT_ALL_ID && !visibleIds.has(i.id),
      );
    } else {
      // SELECT_ALL was just selected (unchecked or indeterminate) — add all visible items
      const withoutSelectAll = currentSelection.filter(i => i.id !== SELECT_ALL_ID);
      newSelection = [...withoutSelectAll, ...visibleItems.filter((item: SelectItem) => !selectedIds.has(item.id))];
    }
    this.updateSelection(newSelection, true);
    this.onChange(SelectWithSearchHelper.getSelectionIds(this._selectionModel, this.multiple));
  }

  onSelectOnlyTheseClick(): void {
    const visibleItems = SelectWithSearchHelper.getVisibleRegularItems(this.items, this._filter);
    this.updateSelection(visibleItems, true);
    this.clearFilter();
    this.onChange(SelectWithSearchHelper.getSelectionIds(this._selectionModel, this.multiple));
  }

  onFixedItemClick(item: SelectItem, selected: boolean): void {
    if (!this.multiple) {
      return;
    }

    if (item.type === SelectItemType.All) {
      const explicitItems = this.getExplicitItems();
      const newSelection = selected ? [item, ...explicitItems, ...this.items] : explicitItems;
      this.updateSelection(newSelection, false);

      this.matSelect?.writeValue(this._selectionModel);
      this.onChange(this.getSelectionIds());
    } else if (item.type === SelectItemType.Explicit) {
      this.updateSelection(this.selectionModel, true);
      this.onChange(SelectWithSearchHelper.getSelectionIds(this._selectionModel, this.multiple));
    }
  }

  onItemClick(): void {
    if (!this.multiple) {
      return;
    }

    // Use setTimeout to allow mat-select to finish updating selectionModel first
    setTimeout(() => {
      const currentSelection = Array.isArray(this.selectionModel) ? [...this.selectionModel] : this.selectionModel;
      const syncedSelection = SelectWithSearchHelper.syncFixedItems(this.selectionModel, this.items, this.fixedItems, this.selectAllItem, this.multiple);

      if (SelectWithSearchHelper.hasSelectionChanged(currentSelection, syncedSelection)) {
        this._selectionModel = syncedSelection;
        this.updateSelectTriggerLabel();

        this.matSelect?.writeValue(syncedSelection);

        this.cdr.markForCheck();
      }

      this.onChange(SelectWithSearchHelper.getSelectionIds(this._selectionModel, this.multiple));
    }, 0);
  }

  // ControlValueAccessor implementation
  writeValue(value: string | string[]): void {
    const selection = SelectWithSearchHelper.getSelectionFromIds(value, this.items, this.fixedItems, this.selectAllItem, this.multiple);
    this.updateSelection(SelectWithSearchHelper.sanitizeSelection(selection), true);
  }

  registerOnChange(onChange: (value: string | string[]) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  private initializeFormControl(): void {
    try {
      const ngControl = this.injector.get(NgControl, null);
      if (!ngControl) {
        this.formControl = null;
        return;
      }

      if (ngControl instanceof FormControlName) {
        const formGroupDirective = this.injector.get(FormGroupDirective, null);
        this.formControl = formGroupDirective?.getControl(ngControl) ?? null;
      } else if (ngControl instanceof FormControlDirective) {
        this.formControl = ngControl.form;
      } else {
        this.formControl = null;
      }

      if (this.formControl) {
        // Use events instead of statusChanges to also catch touched state changes
        // (e.g., when parent form calls markAllAsTouched on save)
        this.formControl.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          this.cdr.markForCheck();
        });
      }
    } catch (error) {
      console.warn('Failed to initialize form control:', error);
      this.formControl = null;
    }
  }

  private createSelectAllItem(): void {
    this.selectAllItem = SelectWithSearchHelper.createSelectAllItem(this.selectAllLabel, this.multiple);
  }

  private updateFixedItemsWithoutGroup(): void {
    this.fixedItemsWithoutGroup = SelectWithSearchHelper.getFixedItemsWithoutGroup(this.fixedItems);
  }

  private resyncSelectionAfterFilterChange(): void {
    if (this.multiple && this._selectionModel !== undefined) {
      this.updateSelection(this._selectionModel, true);
    }
  }

  private normalizeToArray(value: string | string[]): string[] {
    return SelectWithSearchHelper.normalizeToArray(value);
  }

  private get selectionModelAsArray(): SelectItem[] {
    return Array.isArray(this._selectionModel) ? this._selectionModel : [];
  }

  private updateSelectTriggerLabel(): void {
    this.triggerLabel = SelectWithSearchHelper.getTriggerLabel(this.selectionModel, this.items, this.sortItemsByLabel);
  }

  private clearFilter(): void {
    if (this.filterTimeout !== null) {
      clearTimeout(this.filterTimeout);
      this.filterTimeout = null;
    }
    this._filter = '';
    this.filterItems();
    this.cdr.markForCheck();
  }

  private filterItems(): void {
    this.groups = SelectWithSearchHelper.buildGroups(this.items, this.fixedItems, this.filter, this.sortItemsByLabel);
  }

  private updateSelection(selection: Selection, syncFixedItems: boolean): void {
    if (syncFixedItems) {
      selection = SelectWithSearchHelper.syncFixedItems(selection, this.items, this.fixedItems, this.selectAllItem, this.multiple);
    }

    this._selectionModel = selection;
    this.updateSelectTriggerLabel();
    this.cdr.markForCheck();
  }

  private getExplicitItems(): SelectItem[] {
    return this.selectionModelAsArray.filter(i => i.type === SelectItemType.Explicit);
  }

  private notifyTouched(): void {
    this.onTouched?.();
  }

  private getSelectionIds(): string | string[] {
    return SelectWithSearchHelper.getSelectionIds(this._selectionModel, this.multiple);
  }

  private getSelectionFromIds(ids: string | string[]): Selection {
    return SelectWithSearchHelper.getSelectionFromIds(ids, this.items, this.fixedItems, this.selectAllItem, this.multiple);
  }
}
